import React, { createContext, useContext, useState, useEffect } from "react";
import {
  Expense,
  Supplier,
  SupplierPurchase,
  Client,
  ClientTransaction,
  ExpenseFormData,
  SupplierFormData,
  SupplierPurchaseFormData,
  ClientFormData,
  ClientTransactionFormData,
} from "@/types/finance";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useRepairShopContext } from "@/context/RepairShopContext";
import { toast } from "sonner";

interface FinanceContextType {
  expenses: Expense[];
  suppliers: Supplier[];
  supplierPurchases: SupplierPurchase[];
  clients: Client[];
  clientTransactions: ClientTransaction[];
  loading: boolean;

  addExpense: (data: ExpenseFormData) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;

  addSupplier: (data: SupplierFormData) => Promise<Supplier>;
  updateSupplier: (id: string, data: Partial<SupplierFormData>) => Promise<void>;
  deleteSupplier: (id: string) => Promise<void>;

  addSupplierPurchase: (data: SupplierPurchaseFormData) => Promise<void>;
  recordPayment: (purchaseId: string, amount: number) => Promise<void>;

  addClient: (data: ClientFormData) => Promise<Client>;
  updateClient: (id: string, data: Partial<ClientFormData>) => Promise<void>;

  addClientTransaction: (data: ClientTransactionFormData) => Promise<void>;

  refreshAll: () => Promise<void>;
}

const FinanceContext = createContext<FinanceContextType | undefined>(undefined);

export const FinanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { repairShop } = useRepairShopContext();

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierPurchases, setSupplierPurchases] = useState<SupplierPurchase[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientTransactions, setClientTransactions] = useState<ClientTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && repairShop) {
      refreshAll();
    } else {
      setExpenses([]);
      setSuppliers([]);
      setSupplierPurchases([]);
      setClients([]);
      setClientTransactions([]);
      setLoading(false);
    }
  }, [user, repairShop]);

  const refreshAll = async () => {
    if (!user || !repairShop) return;
    setLoading(true);
    try {
      await Promise.all([
        fetchExpenses(),
        fetchSuppliers(),
        fetchSupplierPurchases(),
        fetchClients(),
        fetchClientTransactions(),
      ]);
    } catch (error) {
      console.error("Error refreshing finance data:", error);
    }
    setLoading(false);
  };

  const fetchExpenses = async () => {
    if (!repairShop) return;
    const { data, error } = await supabase
      .from("expenses")
      .select("*")
      .eq("repair_shop_id", repairShop.id)
      .order("date", { ascending: false });

    if (error) throw error;
    setExpenses(data.map(e => ({
      id: e.id,
      description: e.description,
      amount: e.amount,
      category: e.category,
      date: new Date(e.date),
      userId: e.user_id,
      repairShopId: e.repair_shop_id,
      createdAt: new Date(e.created_at),
    })));
  };

  const fetchSuppliers = async () => {
    if (!repairShop) return;
    const { data, error } = await supabase
      .from("suppliers")
      .select("*")
      .eq("repair_shop_id", repairShop.id)
      .order("name");

    if (error) throw error;
    setSuppliers(data.map(s => ({
      id: s.id,
      name: s.name,
      phone: s.phone || undefined,
      email: s.email || undefined,
      address: s.address || undefined,
      repairShopId: s.repair_shop_id,
      createdAt: new Date(s.created_at),
    })));
  };

  const fetchSupplierPurchases = async () => {
    if (!repairShop) return;
    const { data, error } = await supabase
      .from("supplier_purchases")
      .select("*, supplier:suppliers(*)")
      .eq("repair_shop_id", repairShop.id)
      .order("purchase_date", { ascending: false });

    if (error) throw error;
    setSupplierPurchases(data.map(p => ({
      id: p.id,
      supplierId: p.supplier_id,
      supplier: p.supplier ? {
        id: p.supplier.id,
        name: p.supplier.name,
        phone: p.supplier.phone || undefined,
        email: p.supplier.email || undefined,
        address: p.supplier.address || undefined,
        repairShopId: p.supplier.repair_shop_id,
        createdAt: new Date(p.supplier.created_at),
      } : undefined,
      productId: p.product_id,
      quantity: p.quantity,
      totalCost: p.total_cost,
      amountPaid: p.amount_paid,
      amountOwed: p.amount_owed,
      notes: p.notes || undefined,
      purchaseDate: new Date(p.purchase_date),
      userId: p.user_id,
      repairShopId: p.repair_shop_id,
      createdAt: new Date(p.created_at),
    })));
  };

  const fetchClients = async () => {
    if (!repairShop) return;
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("repair_shop_id", repairShop.id)
      .order("name");

    if (error) throw error;
    setClients(data.map(c => ({
      id: c.id,
      name: c.name,
      phone: c.phone,
      email: c.email || undefined,
      creditLimit: c.credit_limit,
      currentBalance: c.current_balance,
      repairShopId: c.repair_shop_id,
      createdAt: new Date(c.created_at),
    })));
  };

  const fetchClientTransactions = async () => {
    if (!repairShop) return;
    const { data, error } = await supabase
      .from("client_transactions")
      .select("*, client:clients(*)")
      .eq("repair_shop_id", repairShop.id)
      .order("created_at", { ascending: false });

    if (error) throw error;
    setClientTransactions(data.map(t => ({
      id: t.id,
      clientId: t.client_id,
      client: t.client ? {
        id: t.client.id,
        name: t.client.name,
        phone: t.client.phone,
        email: t.client.email || undefined,
        creditLimit: t.client.credit_limit,
        currentBalance: t.client.current_balance,
        repairShopId: t.client.repair_shop_id,
        createdAt: new Date(t.client.created_at),
      } : undefined,
      type: t.type as "credit" | "payment" | "purchase",
      amount: t.amount,
      description: t.description,
      receiptNumber: t.receipt_number || undefined,
      userId: t.user_id,
      repairShopId: t.repair_shop_id,
      createdAt: new Date(t.created_at),
    })));
  };

  const addExpense = async (data: ExpenseFormData) => {
    if (!user || !repairShop) throw new Error("User or repair shop not found");

    const { error } = await supabase.from("expenses").insert({
      description: data.description,
      amount: data.amount,
      category: data.category,
      date: data.date.toISOString().split('T')[0],
      user_id: user.id,
      repair_shop_id: repairShop.id,
    });

    if (error) throw error;
    await fetchExpenses();
    toast.success("Expense added");
  };

  const deleteExpense = async (id: string) => {
    const { error } = await supabase.from("expenses").delete().eq("id", id);
    if (error) throw error;
    await fetchExpenses();
    toast.success("Expense deleted");
  };

  const addSupplier = async (data: SupplierFormData): Promise<Supplier> => {
    if (!repairShop) throw new Error("Repair shop not found");

    const { data: inserted, error } = await supabase
      .from("suppliers")
      .insert({
        name: data.name,
        phone: data.phone,
        email: data.email,
        address: data.address,
        repair_shop_id: repairShop.id,
      })
      .select()
      .single();

    if (error) throw error;
    await fetchSuppliers();
    toast.success("Supplier added");
    return {
      id: inserted.id,
      name: inserted.name,
      phone: inserted.phone || undefined,
      email: inserted.email || undefined,
      address: inserted.address || undefined,
      repairShopId: inserted.repair_shop_id,
      createdAt: new Date(inserted.created_at),
    };
  };

  const updateSupplier = async (id: string, data: Partial<SupplierFormData>) => {
    const { error } = await supabase
      .from("suppliers")
      .update({
        name: data.name,
        phone: data.phone,
        email: data.email,
        address: data.address,
      })
      .eq("id", id);

    if (error) throw error;
    await fetchSuppliers();
    toast.success("Supplier updated");
  };

  const deleteSupplier = async (id: string) => {
    const { error } = await supabase.from("suppliers").delete().eq("id", id);
    if (error) throw error;
    await fetchSuppliers();
    toast.success("Supplier deleted");
  };

  const addSupplierPurchase = async (data: SupplierPurchaseFormData) => {
    if (!user || !repairShop) throw new Error("User or repair shop not found");

    const amountOwed = data.totalCost - data.amountPaid;

    const { error } = await supabase.from("supplier_purchases").insert({
      supplier_id: data.supplierId,
      product_id: data.productId,
      quantity: data.quantity,
      total_cost: data.totalCost,
      amount_paid: data.amountPaid,
      amount_owed: amountOwed,
      notes: data.notes,
      purchase_date: data.purchaseDate.toISOString().split('T')[0],
      user_id: user.id,
      repair_shop_id: repairShop.id,
    });

    if (error) throw error;

    if (data.addToStock) {
      const { data: product } = await supabase
        .from("products")
        .select("stock_quantity")
        .eq("id", data.productId)
        .single();

      if (product) {
        await supabase
          .from("products")
          .update({ stock_quantity: product.stock_quantity + data.quantity })
          .eq("id", data.productId);
      }
    }

    await fetchSupplierPurchases();
    toast.success("Purchase recorded");
  };

  const recordPayment = async (purchaseId: string, amount: number) => {
    console.log("Recording payment:", { purchaseId, amount });
    
    const { data: purchase, error: fetchError } = await supabase
      .from("supplier_purchases")
      .select("*")
      .eq("id", purchaseId)
      .single();

    console.log("Fetched purchase:", purchase);

    if (fetchError || !purchase) {
      console.error("Fetch error:", fetchError);
      toast.error("Purchase not found");
      throw new Error("Purchase not found");
    }

    const newAmountPaid = purchase.amount_paid + amount;
    const newAmountOwed = Math.max(0, purchase.total_cost - newAmountPaid);

    console.log("Updating to:", { newAmountPaid, newAmountOwed });

    const { data: updated, error } = await supabase
      .from("supplier_purchases")
      .update({
        amount_paid: newAmountPaid,
        amount_owed: newAmountOwed,
      })
      .eq("id", purchaseId)
      .select();

    console.log("Update result:", { updated, error });

    if (error) {
      console.error("Payment error:", error);
      toast.error("Failed to record payment");
      throw error;
    }
    
    await fetchSupplierPurchases();
    toast.success(`Payment of ${amount.toFixed(2)} DT recorded`);
  };

  const addClient = async (data: ClientFormData): Promise<Client> => {
    if (!repairShop) throw new Error("Repair shop not found");

    const { data: inserted, error } = await supabase
      .from("clients")
      .insert({
        name: data.name,
        phone: data.phone,
        email: data.email,
        credit_limit: data.creditLimit,
        current_balance: 0,
        repair_shop_id: repairShop.id,
      })
      .select()
      .single();

    if (error) throw error;
    await fetchClients();
    toast.success("Client added");
    return {
      id: inserted.id,
      name: inserted.name,
      phone: inserted.phone,
      email: inserted.email || undefined,
      creditLimit: inserted.credit_limit,
      currentBalance: inserted.current_balance,
      repairShopId: inserted.repair_shop_id,
      createdAt: new Date(inserted.created_at),
    };
  };

  const updateClient = async (id: string, data: Partial<ClientFormData>) => {
    const { error } = await supabase
      .from("clients")
      .update({
        name: data.name,
        phone: data.phone,
        email: data.email,
        credit_limit: data.creditLimit,
      })
      .eq("id", id);

    if (error) throw error;
    await fetchClients();
    toast.success("Client updated");
  };

  const addClientTransaction = async (data: ClientTransactionFormData) => {
    if (!user || !repairShop) throw new Error("User or repair shop not found");

    const { error } = await supabase.from("client_transactions").insert({
      client_id: data.clientId,
      type: data.type,
      amount: data.amount,
      description: data.description,
      user_id: user.id,
      repair_shop_id: repairShop.id,
    });

    if (error) throw error;
    await Promise.all([fetchClientTransactions(), fetchClients()]);
    toast.success("Transaction recorded");
  };

  return (
    <FinanceContext.Provider
      value={{
        expenses,
        suppliers,
        supplierPurchases,
        clients,
        clientTransactions,
        loading,
        addExpense,
        deleteExpense,
        addSupplier,
        updateSupplier,
        deleteSupplier,
        addSupplierPurchase,
        recordPayment,
        addClient,
        updateClient,
        addClientTransaction,
        refreshAll,
      }}
    >
      {children}
    </FinanceContext.Provider>
  );
};

export const useFinance = () => {
  const context = useContext(FinanceContext);
  if (!context) {
    throw new Error("useFinance must be used within a FinanceProvider");
  }
  return context;
};
